import { transferApi } from './transferApi'
import {
  adapterSavingGoal,
  adapterGoalTransfer,
  adapterCashbackInfo,
} from './transferAdapters'
import {
  GetEstimateConvertOrSellFeePayload,
  EstimateConvertOrSellFeeDTO,
  SendConvertOrSellPayload,
  SendConvertOrSellDTO,
  OnRampPricePerUnitParams,
  OnRampPricePerUnitDTO,
  ExternalWithdrawCalcFeesPayload,
  ExternalWithdrawFeeDTO,
  ExternalWithdrawPayload,
  ExternalWithdrawDTO,
  CheckExternalWalletAddressPayload,
  OnRampCryptoPriceQuotesDTO,
  AdaptedOnRampCryptoPriceQuotesDTO,
  CreatePurchaseOnRampPayload,
  CreatePurchaseOnRampDTO,
  AdaptedSavingGoalDTO,
  SavingGoalByIDParams,
  CreateSavingGoalPayload,
  UpdateSavingGoalPayload,
  SavingGoalTransferInPayload,
  SavingGoalTransferOutPayload,
  SavingGoalDTO,
  EstimateSavingTransferOutFeePayload,
  AdaptedEstimateSavingTransferOutFeeDTO,
  FetchSavingGoalHistoryAdaptedParams,
  FetchSavingGoalHistoryParams,
  AdaptedGoalTransferDTO,
  GetSavingGoalTransferDetailsParams,
  CashbackInfoParams,
  AdaptedCashbackInfoDTO,
  CreatePurchaseOnRampDTOLink,
} from './transferTypes'

/**
 * Calculate conversion fee
 */
async function calculateConversionFee(
  payload: GetEstimateConvertOrSellFeePayload,
): Promise<EstimateConvertOrSellFeeDTO> {
  return transferApi.calculateConversionFee(payload)
}

/**
 * Execute conversion
 */
async function sendConvertOrSell(
  payload: SendConvertOrSellPayload,
): Promise<SendConvertOrSellDTO> {
  return transferApi.sendConvertOrSell(payload)
}

/**
 * Get OnRamp price per unit
 */
async function getOnRampPricePerUnit(
  payload: OnRampPricePerUnitParams,
): Promise<OnRampPricePerUnitDTO> {
  return transferApi.getOnRampPricePerUnit(payload)
}

/**
 * Calculate external withdraw fee
 */
async function calculateExternalWithdrawFee(
  payload: ExternalWithdrawCalcFeesPayload,
): Promise<ExternalWithdrawFeeDTO> {
  return transferApi.calculateExternalWithdrawFee(payload)
}

/**
 * Create external withdraw
 */
async function createExternalWithdraw(
  payload: ExternalWithdrawPayload,
): Promise<ExternalWithdrawDTO> {
  return transferApi.createExternalWithdraw(payload)
}

/**
 * Check external wallet address
 */
async function checkExternalWalletAddress(
  payload: CheckExternalWalletAddressPayload,
): Promise<void> {
  return transferApi.checkExternalWalletAddress(payload)
}

/**
 * Get crypto price quotes for on-ramp with adapter
 */
async function getCryptoPriceQuotesByCurrency(
  payload: OnRampCryptoPriceQuotesDTO,
): Promise<AdaptedOnRampCryptoPriceQuotesDTO> {
  const onRampCryptoPriceQuotes =
    await transferApi.getCryptoPriceQuotesByCurrency(payload)

  // Use the real adapter instead of mock data
  return {
    ...onRampCryptoPriceQuotes,
    formattedFiatFee: '0.00 USD', // Will be set by the adapter when used
    formattedAmountWithPaymentCurrencyRelative: '',
    formattedFiatTotal: '0.00 USD',
    priceQuotesByPaymentMethod: onRampCryptoPriceQuotes.price_quotes[0],
  }
}

/**
 * Create purchase on-ramp
 */
async function createPurchaseOnRamp(
  payload: CreatePurchaseOnRampPayload,
): Promise<CreatePurchaseOnRampDTO> {
  // For web, we use the on-ramp-webview URL for 3DS redirect
  // This page has 3DS parameter handling logic in CheckoutFlow.tsx
  const webRedirectUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}/on-ramp-webview`
      : undefined

  return transferApi.createPurchaseOnRamp({
    ...payload,
    redirectUrl: payload.redirectUrl || webRedirectUrl,
  })
}

/**
 * Create purchase on-ramp link
/**
 * Create purchase on-ramp link
 */
async function createPurchaseOnRampLink(
  payload: CreatePurchaseOnRampPayload,
): Promise<CreatePurchaseOnRampDTOLink> {
  return transferApi.createPurchaseOnRampLink(payload)
}
/*
 * SAVINGS SERVICES
 */

/**
 * Fetch all saving goals with data adaptation
 */
async function fetchSavingGoals(): Promise<{
  saving_goals: AdaptedSavingGoalDTO[]
}> {
  const data = await transferApi.fetchSavingGoals()

  return {
    ...data,
    saving_goals: data.saving_goals.map(adapterSavingGoal),
  }
}

/**
 * Get single saving goal by ID with data adaptation
 */
async function getSavingGoal(
  params: SavingGoalByIDParams,
): Promise<AdaptedSavingGoalDTO> {
  const data = await transferApi.getSavingGoal(params)
  return adapterSavingGoal(data)
}

/**
 * Create new saving goal
 */
async function createSavingGoal(
  payload: CreateSavingGoalPayload,
): Promise<SavingGoalDTO> {
  return transferApi.createSavingGoal(payload)
}

/**
 * Update existing saving goal
 */
async function updateSavingGoal(
  payload: UpdateSavingGoalPayload,
): Promise<void> {
  return transferApi.updateSavingGoal(payload)
}

/**
 * Delete saving goal
 */
async function deleteSavingGoal(params: SavingGoalByIDParams): Promise<void> {
  return transferApi.deleteSavingGoal(params)
}

/**
 * Create goal transfer in (deposit to goal)
 */
async function createGoalTransferIn(
  payload: SavingGoalTransferInPayload,
): Promise<void> {
  return transferApi.createGoalTransferIn(payload)
}

/**
 * Create goal transfer out (withdraw from goal)
 */
async function createGoalTransferOut(
  payload: SavingGoalTransferOutPayload,
): Promise<void> {
  return transferApi.createGoalTransferOut(payload)
}

/**
 * Fetch saving goal history with data adaptation
 */
async function fetchSavingGoalHistory({
  from_date,
  to_date,
  ...restParams
}: FetchSavingGoalHistoryAdaptedParams): Promise<{
  transfers: AdaptedGoalTransferDTO[]
  next_page_id: number | null
}> {
  try {
    // Convert dates to timestamps like in RN version
    const apiParams: FetchSavingGoalHistoryParams = {
      ...restParams,
      from_date: from_date ? new Date(from_date).getTime() : undefined,
      to_date: to_date ? new Date(to_date).getTime() : undefined,
    }

    const response = await transferApi.fetchSavingGoalHistory(apiParams)
    const transfers = response.transfers.map(adapterGoalTransfer)

    const result = {
      transfers,
      next_page_id: response.next_page_id,
    }

    return result
  } catch (error) {
    console.error('Error in fetchSavingGoalHistory:', error)
    throw error
  }
}

/**
 * Get saving goal transfer details with data adaptation
 */
async function getSavingGoalTransferDetails(
  params: GetSavingGoalTransferDetailsParams,
): Promise<AdaptedGoalTransferDTO> {
  const data = await transferApi.getSavingGoalTransferDetails(params)
  return adapterGoalTransfer(data)
}

/**
 * CASHBACK SERVICES
 */

/**
 * Get cashback info with data adaptation
 */
async function getCashbackInfo(
  params: CashbackInfoParams,
): Promise<AdaptedCashbackInfoDTO> {
  const data = await transferApi.getCashbackInfo(params)
  return adapterCashbackInfo(data)
}

export const transferServices = {
  calculateConversionFee,
  sendConvertOrSell,
  getOnRampPricePerUnit,
  calculateExternalWithdrawFee,
  createExternalWithdraw,
  checkExternalWalletAddress,
  getCryptoPriceQuotesByCurrency,
  createPurchaseOnRamp,

  // SAVINGS
  fetchSavingGoals,
  getSavingGoal,
  createSavingGoal,
  updateSavingGoal,
  deleteSavingGoal,
  createGoalTransferIn,
  createGoalTransferOut,
  fetchSavingGoalHistory,
  getSavingGoalTransferDetails,
  estimateSavingTransferOutFee,
  createPurchaseOnRampLink,
  // CASHBACK
  getCashbackInfo,

  // OFF-RAMP
  getOffRampPriceLimits: transferApi.getOffRampPriceLimits,
  getOffRampPriceEstimate: transferApi.getOffRampPriceEstimate,
  getOffRampPriceQuotes: transferApi.getOffRampPriceQuotes,
  initiateOffRamp: transferApi.initiateOffRamp,
  confirmOffRampPayment: transferApi.confirmOffRampPayment,
  cancelOffRamp: transferApi.cancelOffRamp,
}

/**
 * Estimate saving transfer out fee with adapter
 */
async function estimateSavingTransferOutFee(
  payload: EstimateSavingTransferOutFeePayload,
): Promise<AdaptedEstimateSavingTransferOutFeeDTO> {
  const transferOutFees =
    await transferApi.estimateSavingTransferOutFee(payload)

  // Simple adapter - format fees for display
  const formattedTransactionFee = `${transferOutFees.fee.amount} ${transferOutFees.fee.currency}`
  const formattedTotalReceived = `${transferOutFees.amount.amount} ${transferOutFees.amount.currency}`

  return {
    ...transferOutFees,
    formattedTransactionFee,
    formattedTotalReceived,
  }
}
